import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { BackgroundProcessObjectType } from "./backgroundProcessingApi";
import { isFinalStatus, isRunningStatus } from "./backgroundProcessStatus";

/**
 * backgroundProcessing slice — the registry the global poller and the UI share.
 *
 * `byKey` holds the latest known state of each tracked process; `subscribers` is
 * a per-key refcount driven by `useBackgroundProcess` (mount = subscribe). The
 * poller middleware (backgroundPollerMiddleware) polls exactly the keys that are
 * BOTH subscribed AND not yet in a final state, so an idle screen costs nothing
 * and a finished job stops polling itself.
 */

export interface TrackedProcess {
  objectType: BackgroundProcessObjectType;
  objectId: string;
  label?: string;
  status?: string;
  operationType?: string;
  processNumber?: number;
}

export const processKey = (
  objectType: BackgroundProcessObjectType | string,
  objectId: string,
): string => `${objectType}:${objectId}`;

interface BackgroundProcessingState {
  byKey: Record<string, TrackedProcess>;
  subscribers: Record<string, number>;
  /** Keys whose job must be watched to completion regardless of any component
   *  being mounted (set at launch / re-attach, cleared on completion). This is
   *  what makes completion location-independent. */
  persistent: Record<string, boolean>;
  /** key -> the run token (processNumber, or "final" when unknown) whose
   *  completion side effect has already been handled. Stored here rather than in
   *  a component ref so it survives a launcher unmount/remount: a finished run
   *  won't re-trigger its onCompleted refresh if the screen happens to remount. */
  handledCompletion: Record<string, number | "final">;
}

const initialState: BackgroundProcessingState = {
  byKey: {},
  subscribers: {},
  persistent: {},
  handledCompletion: {},
};

const backgroundProcessingSlice = createSlice({
  name: "backgroundProcessing",
  initialState,
  reducers: {
    subscribeProcess: (state, action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string }>) => {
      const key = processKey(action.payload.objectType, action.payload.objectId);
      state.subscribers[key] = (state.subscribers[key] ?? 0) + 1;
    },
    unsubscribeProcess: (state, action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string }>) => {
      const key = processKey(action.payload.objectType, action.payload.objectId);
      const next = (state.subscribers[key] ?? 0) - 1;
      if (next <= 0) delete state.subscribers[key];
      else state.subscribers[key] = next;
    },
    /** Merge the latest known state for a process (from launch, re-attach or a poll tick). */
    upsertProcess: (state, action: PayloadAction<TrackedProcess>) => {
      const key = processKey(action.payload.objectType, action.payload.objectId);
      state.byKey[key] = { ...state.byKey[key], ...action.payload };
    },
    /** Semantic completion event — the poller emits this once per finished run.
     *  Feature listeners (refresh + toast) subscribe to it; see migration plan. */
    processCompleted: (state, action: PayloadAction<TrackedProcess>) => {
      const key = processKey(action.payload.objectType, action.payload.objectId);
      state.byKey[key] = { ...state.byKey[key], ...action.payload };
    },
    clearProcess: (state, action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string }>) => {
      delete state.byKey[processKey(action.payload.objectType, action.payload.objectId)];
    },
    /** Keep watching this job to completion even if no component is mounted. */
    markPersistent: (state, action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string }>) => {
      state.persistent[processKey(action.payload.objectType, action.payload.objectId)] = true;
    },
    clearPersistent: (state, action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string }>) => {
      delete state.persistent[processKey(action.payload.objectType, action.payload.objectId)];
    },
    /** Record that this run's completion side effect (the screen refresh) has run,
     *  so it fires once per run even across launcher remounts. */
    markCompletionHandled: (
      state,
      action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string; token: number | "final" }>,
    ) => {
      state.handledCompletion[processKey(action.payload.objectType, action.payload.objectId)] = action.payload.token;
    },
    /** Forget the handled marker so a freshly launched / re-attached run can fire
     *  its completion side effect again. */
    clearCompletionHandled: (state, action: PayloadAction<{ objectType: BackgroundProcessObjectType; objectId: string }>) => {
      delete state.handledCompletion[processKey(action.payload.objectType, action.payload.objectId)];
    },
  },
});

export const {
  subscribeProcess,
  unsubscribeProcess,
  upsertProcess,
  processCompleted,
  clearProcess,
  markPersistent,
  clearPersistent,
  markCompletionHandled,
  clearCompletionHandled,
} = backgroundProcessingSlice.actions;

const selectState = (state: RootState) => state.backgroundProcessing;

export const selectProcess = (
  state: RootState,
  objectType: BackgroundProcessObjectType,
  objectId: string,
): TrackedProcess | undefined => selectState(state).byKey[processKey(objectType, objectId)];

/** The run token whose completion side effect has already been handled for this
 *  key, or undefined if none yet. Compared against the live run token to fire
 *  onCompleted exactly once per run. */
export const selectHandledCompletion = (
  state: RootState,
  objectType: BackgroundProcessObjectType,
  objectId: string,
): number | "final" | undefined => selectState(state).handledCompletion[processKey(objectType, objectId)];

/** Processes that still need watching: a component is subscribed OR the job was
 *  marked persistent (in flight), AND there is a non-final entry. The poller
 *  drives its single interval off this — empty ⇒ no timer. */
export const selectPollableProcesses = (state: RootState): TrackedProcess[] => {
  const { byKey, subscribers, persistent } = selectState(state);
  const keys = new Set([...Object.keys(subscribers), ...Object.keys(persistent)]);
  return [...keys]
    .map((key) => byKey[key])
    .filter((entry): entry is TrackedProcess => !!entry && !isFinalStatus(entry.status));
};

export const selectIsAnyProcessRunning = (state: RootState): boolean =>
  Object.values(selectState(state).byKey).some((p) => isRunningStatus(p.status));

export default backgroundProcessingSlice.reducer;
